import { useState } from 'react';
import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { Calendar } from './Calendar';

/**
 * Week view — highlights a retroactive 7-day range ending on the selected date.
 *
 * Visual band logic:
 *   - All 7 days get a light accent background strip.
 *   - The strip gets a left rounded-cap at the range start OR at any Sunday (row
 *     boundary) so the band looks continuous across row breaks.
 *   - Symmetrically, a right cap appears at the range end OR any Saturday.
 *   - The end day (the anchor) gets the full accent circle on top of the strip.
 *
 * The preview band follows the mouse on hover and the keyboard focus otherwise,
 * so arrow-key users see the same range preview as mouse users.
 */
export function WeekPicker({ selectedDate, onSelect, maxDate, daysWithData = [], onViewMonthChange, monthsWithData = [], yearsWithData = [] }) {
    const today = dayjs();
    const [hoveredDate, setHoveredDate] = useState(null);

    // Is `day` within the 7-day retroactive range ending on `anchor`?
    const isInRange = (day, anchor) => {
        if (!anchor) return false;
        const start = anchor.subtract(6, 'day');
        return !day.isBefore(start, 'day') && !day.isAfter(anchor, 'day');
    };

    const isRangeEnd = (day, anchor) =>
        anchor ? day.isSame(anchor, 'day') : false;

    const renderDay = (day, viewMonth, { tabIndex, focusedDate }) => {
        // Mouse hover takes precedence; otherwise the keyboard-focused day
        // drives the preview band so arrow navigation previews the range too.
        const previewAnchor = hoveredDate ?? focusedDate;

        const isInSelectedRange = isInRange(day, selectedDate);
        const isInHoveredRange = isInRange(day, previewAnchor);
        const isSelectedRangeEnd = isRangeEnd(day, selectedDate);
        const isHoveredRangeEnd = isRangeEnd(day, previewAnchor); // used for row cap logic
        const isToday = day.isSame(today, 'day');
        const isOutside = !day.isSame(viewMonth, 'month');
        const isDisabled = maxDate ? day.isAfter(maxDate, 'day') : false;
        const isHighlighted = !isOutside && daysWithData.includes(day.format('YYYY-MM-DD'));

        // Row boundaries — used to cap the band when it wraps to a new row
        const isSunday = day.day() === 0;
        const isSaturday = day.day() === 6;

        // Left cap: range start day OR first day of a new row mid-range
        const isSelLeftCap = isInSelectedRange && (day.isSame(selectedDate.subtract(6, 'day'), 'day') || isSunday);
        const isHovLeftCap = isInHoveredRange && previewAnchor && (day.isSame(previewAnchor.subtract(6, 'day'), 'day') || isSunday);

        // Right cap: range end day OR last day of a row mid-range
        const isSelRightCap = isInSelectedRange && (isSelectedRangeEnd || isSaturday);
        const isHovRightCap = isInHoveredRange && (isHoveredRangeEnd || isSaturday);

        return (
            <div
                key={day.toString()}
                role="gridcell"
                aria-selected={isSelectedRangeEnd}
                className={cn(
                    'relative flex h-9 items-center justify-center',
                    // Selected band
                    isInSelectedRange && 'bg-accent/20',
                    isInSelectedRange && isSelLeftCap && 'rounded-l-full',
                    isInSelectedRange && isSelRightCap && 'rounded-r-full',
                )}
                onMouseEnter={() => !isDisabled && setHoveredDate(day)}
                onMouseLeave={() => setHoveredDate(null)}
            >
                {isInHoveredRange && (
                    <span className={cn(
                        'absolute inset-0 z-0 bg-accent/50',
                        isHovLeftCap && 'rounded-l-full',
                        isHovRightCap && 'rounded-r-full',
                    )} />
                )}
                <button
                    type="button"
                    tabIndex={tabIndex}
                    onClick={() => !isDisabled && onSelect(day)}
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    aria-current={isToday ? 'date' : undefined}
                    aria-label={`Week ending ${day.format('dddd, MMMM D, YYYY')}`}
                    data-date={day.format('YYYY-MM-DD')}
                    className={cn(
                        'relative z-10 flex h-9 w-9 flex-col items-center justify-center rounded-full text-sm transition-colors',
                        // Muted text for days outside the viewed month
                        isOutside && 'text-muted-foreground opacity-50',
                        isDisabled && 'pointer-events-none opacity-30'
                    )}
                >
                    <span className={isHighlighted ? '-translate-y-px' : ''}>
                        {day.date()}
                    </span>
                    {isHighlighted && (
                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute bottom-1 h-1 w-1 rounded-full',
                                isInSelectedRange ? 'bg-accent-foreground' : 'bg-accent'
                            )}
                        />
                    )}
                </button>
            </div>
        );
    };

    return (
        <Calendar
            selectedDate={selectedDate}
            renderDay={renderDay}
            maxDate={maxDate}
            onViewMonthChange={onViewMonthChange}
            monthsWithData={monthsWithData}
            yearsWithData={yearsWithData}
        />
    );
}
